// Backend-heavy batch translation service with real-time progress tracking
import { supabase } from '../lib/supabase';

export interface BatchTranslationRequest {
  items: Array<{
    key_id: string;
    source_text: string;
    source_language: string;
    target_language: string;
  }>;
  project_id: string;
  team_id?: string;
}

export interface BatchTranslationProgress {
  id: string;
  project_id: string;
  team_id?: string;
  user_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  total_items: number;
  completed_items: number;
  failed_items: number;
  progress_percentage: number;
  current_chunk: number;
  total_chunks: number;
  source_language: string;
  target_language: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface TranslationCompletionState {
  language: string;
  isComplete: boolean;
  totalKeys: number;
  translatedKeys: number;
  missingKeys: number;
  lastVerified: string;
}

class BatchTranslationService {
  private static instance: BatchTranslationService;
  private activeSubscriptions = new Map<string, any>();
  private completionStates = new Map<string, TranslationCompletionState>();
  private verificationIntervals = new Map<string, NodeJS.Timeout>();

  public static getInstance(): BatchTranslationService {
    if (!BatchTranslationService.instance) {
      BatchTranslationService.instance = new BatchTranslationService();
    }
    return BatchTranslationService.instance;
  }

  private constructor() {}

  /**
   * Start a batch translation job on the backend
   * @param request The translation request
   * @returns The batch job ID for tracking progress
   */
  public async startBatchTranslation(request: BatchTranslationRequest): Promise<string> {
    try {
      console.log('🚀 Starting batch translation with request:', {
        itemsCount: request.items?.length || 0,
        projectId: request.project_id,
        teamId: request.team_id,
        sampleItems: request.items?.slice(0, 2) || []
      });

      // Call the new Supabase Edge Function for batch translation
      const response = await supabase.functions.invoke('batch-translate', {
        body: request
      });

      console.log('📡 Batch translation response:', response);

      if (response.error) {
        console.error('❌ Batch translation error response:', response.error);
        throw new Error(`Batch translation failed: ${response.error.message}`);
      }

      const { batch_id } = response.data;
      console.log('✅ Batch translation started successfully with ID:', batch_id);
      return batch_id;
    } catch (error: any) {
      console.error('❌ Error starting batch translation:', error);
      
      // Log more details about the error
      if (error.message) {
        console.error('Error message:', error.message);
      }
      if (error.status) {
        console.error('Error status:', error.status);
      }
      if (error.statusText) {
        console.error('Error status text:', error.statusText);
      }
      
      throw error;
    }
  }

  /**
   * Subscribe to real-time progress updates for a batch translation
   * @param batchId The batch job ID
   * @param onProgress Callback for progress updates
   * @param onComplete Callback when translation is complete
   * @param onError Callback for errors
   */
  public subscribeToProgress(
    batchId: string,
    callbacks: {
      onProgress?: (progress: BatchTranslationProgress) => void;
      onComplete?: (progress: BatchTranslationProgress) => void;
      onError?: (error: string) => void;
    }
  ): () => void {
    console.log(`📡 Setting up real-time subscription for batch job: ${batchId}`);
    
    // Subscribe to real-time progress updates
    const progressSubscription = supabase
      .channel(`batch-translation-${batchId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'BatchTranslationJobs',
          filter: `id=eq.${batchId}`
        },
        (payload) => {
          console.log(`📡 Real-time BatchTranslationJobs update for ${batchId}:`, {
            status: payload.new?.status,
            progress_percentage: payload.new?.progress_percentage,
            completed_items: payload.new?.completed_items,
            total_items: payload.new?.total_items
          });
          
          const progress = payload.new as BatchTranslationProgress;
          
          if (callbacks.onProgress) {
            callbacks.onProgress(progress);
          }

          // Check if translation is complete
          if (progress.status === 'completed') {
            console.log(`✅ Real-time completion detected for ${batchId}`);
            if (callbacks.onComplete) {
              callbacks.onComplete(progress);
            }
            this.unsubscribe(batchId);
          } else if (progress.status === 'failed') {
            console.log(`❌ Real-time failure detected for ${batchId}`);
            if (callbacks.onError) {
              callbacks.onError(progress.error_message || 'Translation failed');
            }
            this.unsubscribe(batchId);
          } else if (progress.status === 'cancelled') {
            console.log(`🚫 Real-time cancellation detected for ${batchId}`);
            if (callbacks.onError) {
              callbacks.onError('Translation was cancelled');
            }
            this.unsubscribe(batchId);
          }
        }
      )
      .subscribe((status) => {
        console.log(`📡 Subscription status for ${batchId}:`, status);
      });

    // Store subscription for cleanup
    this.activeSubscriptions.set(batchId, progressSubscription);

    // Return unsubscribe function
    return () => this.unsubscribe(batchId);
  }

  /**
   * Verify translation completion state for a language
   * @param projectId The project ID
   * @param targetLanguage The target language to verify
   * @param sourceLanguage The source language
   * @returns Completion state with actual translation counts
   */
  public async verifyTranslationCompletion(
    projectId: string,
    targetLanguage: string,
    sourceLanguage: string
  ): Promise<TranslationCompletionState> {
    try {
      // Get all translation keys for the project
      const { data: keys, error: keysError } = await supabase
        .from('TranslationKeys')
        .select('id')
        .eq('project_id', projectId);

      if (keysError) {
        throw keysError;
      }

      const totalKeys = keys?.length || 0;

      // Get all source language strings that have content and valid keys
      const { data: sourceStrings, error: sourceError } = await supabase
        .from('TranslationStrings')
        .select(`
          key_id,
          TranslationKeys!inner (
            id,
            project_id
          )
        `)
        .eq('project_id', projectId)
        .eq('language_code', sourceLanguage)
        .not('value', 'is', null)
        .not('value', 'eq', '');

      if (sourceError) {
        throw sourceError;
      }

      // Use Set to ensure unique key_ids
      const uniqueSourceKeys = new Set(sourceStrings?.map(s => s.key_id) || []);
      const keysWithSourceContent = uniqueSourceKeys.size;

      // If no source content exists, the language is complete (nothing to translate)
      if (keysWithSourceContent === 0) {
        return {
          language: targetLanguage,
          isComplete: true,
          totalKeys,
          translatedKeys: 0,
          missingKeys: 0,
          lastVerified: new Date().toISOString()
        };
      }

      // Get all target language strings that have content and valid keys
      const { data: targetStrings, error: targetError } = await supabase
        .from('TranslationStrings')
        .select(`
          key_id,
          TranslationKeys!inner (
            id,
            project_id
          )
        `)
        .eq('project_id', projectId)
        .eq('language_code', targetLanguage)
        .not('value', 'is', null)
        .not('value', 'eq', '');

      if (targetError) {
        throw targetError;
      }

      // Use Set to ensure unique key_ids and only count keys that have source content
      const uniqueTargetKeys = new Set(targetStrings?.map(s => s.key_id) || []);
      const translatedKeys = Array.from(uniqueTargetKeys).filter(keyId => 
        uniqueSourceKeys.has(keyId)
      ).length;
      const missingKeys = Math.max(0, keysWithSourceContent - translatedKeys);
      const isComplete = missingKeys === 0;

      // Debug logging to help identify data inconsistencies
      console.log(`🔍 Completion verification for ${targetLanguage}:`, {
        totalProjectKeys: totalKeys,
        keysWithSourceContent,
        translatedKeys,
        missingKeys,
        isComplete,
        completionPercentage: keysWithSourceContent > 0 ? Math.round((translatedKeys / keysWithSourceContent) * 100) : 100
      });

      const completionState: TranslationCompletionState = {
        language: targetLanguage,
        isComplete,
        totalKeys: keysWithSourceContent, // Use keys with source content as the total
        translatedKeys,
        missingKeys,
        lastVerified: new Date().toISOString()
      };

      // Cache the completion state
      this.completionStates.set(`${projectId}-${targetLanguage}`, completionState);

      return completionState;
    } catch (error) {
      console.error('Error verifying translation completion:', error);
      throw error;
    }
  }

  /**
   * Get cached completion state for a language
   * @param projectId The project ID
   * @param targetLanguage The target language
   * @returns Cached completion state or null if not cached
   */
  public getCachedCompletionState(
    projectId: string,
    targetLanguage: string
  ): TranslationCompletionState | null {
    return this.completionStates.get(`${projectId}-${targetLanguage}`) || null;
  }

  /**
   * Set up periodic verification for a completed translation
   * @param projectId The project ID
   * @param targetLanguage The target language
   * @param sourceLanguage The source language
   * @param onStateChange Callback when completion state changes
   */
  public setupCompletionVerification(
    projectId: string,
    targetLanguage: string,
    sourceLanguage: string,
    onStateChange: (state: TranslationCompletionState) => void
  ): () => void {
    const verificationKey = `${projectId}-${targetLanguage}`;
    
    // Clear any existing verification interval
    const existingInterval = this.verificationIntervals.get(verificationKey);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Set up periodic verification every 60 seconds (increased from 30 seconds)
    const interval = setInterval(async () => {
      try {
        const newState = await this.verifyTranslationCompletion(
          projectId,
          targetLanguage,
          sourceLanguage
        );
        
        const cachedState = this.completionStates.get(verificationKey);
        
        // Only trigger callback if state has changed
        if (!cachedState || 
            cachedState.isComplete !== newState.isComplete ||
            cachedState.translatedKeys !== newState.translatedKeys ||
            cachedState.missingKeys !== newState.missingKeys) {
          
          onStateChange(newState);
        }
      } catch (error) {
        console.error('Error in completion verification:', error);
      }
    }, 30000);

    this.verificationIntervals.set(verificationKey, interval);

    // Return cleanup function
    return () => {
      clearInterval(interval);
      this.verificationIntervals.delete(verificationKey);
    };
  }

  /**
   * Cancel a batch translation job
   * @param batchId The batch job ID
   * @returns Promise that resolves when cancellation is complete
   */
  public async cancelBatchTranslation(batchId: string): Promise<void> {
    try {
      const response = await supabase.functions.invoke(`batch-translate/${batchId}`, {
        method: 'DELETE'
      });

      if (response.error) {
        throw new Error(`Failed to cancel batch translation: ${response.error.message}`);
      }

      console.log(`✅ Batch translation ${batchId} cancelled successfully`);
    } catch (error) {
      console.error('Error cancelling batch translation:', error);
      throw error;
    }
  }

  /**
   * Get current progress for a batch translation
   * @param batchId The batch job ID
   * @returns Current progress information
   */
  public async getProgress(batchId: string): Promise<BatchTranslationProgress | null> {
    try {
      const { data, error } = await supabase
        .from('BatchTranslationJobs')
        .select('*')
        .eq('id', batchId)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching progress:', error);
      return null;
    }
  }

  /**
   * Subscribe to real-time translation string updates for immediate UI feedback
   * @param projectId The project ID to watch for updates
   * @param onTranslationUpdate Callback when a translation is completed
   * @returns Unsubscribe function
   */
  public subscribeToTranslationUpdates(
    projectId: string, 
    onTranslationUpdate: (translation: any) => void
  ): () => void {
    const channelName = `translation-updates-${projectId}`;
    
    // Rate limiting to prevent browser resource exhaustion
    let updateCount = 0;
    let lastResetTime = Date.now();
    const MAX_UPDATES_PER_SECOND = 5; // Reduced from 10
    const RESET_INTERVAL = 2000; // 2 seconds (increased from 1 second)
    
    const rateLimitedCallback = (payload: any) => {
      const now = Date.now();
      
      // Reset counter every second
      if (now - lastResetTime > RESET_INTERVAL) {
        updateCount = 0;
        lastResetTime = now;
      }
      
      // Only process if under rate limit
      if (updateCount < MAX_UPDATES_PER_SECOND) {
        updateCount++;
        console.log('🔄 Real-time translation update (rate limited):', payload);
        onTranslationUpdate(payload.new);
      } else {
        console.log('⚠️ Rate limit exceeded for translation updates, skipping update');
      }
    };
    
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'TranslationStrings',
          filter: `project_id=eq.${projectId}`
        },
        rateLimitedCallback
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'TranslationStrings',
          filter: `project_id=eq.${projectId}`
        },
        rateLimitedCallback
      )
      .subscribe();

    // Store subscription for cleanup
    this.activeSubscriptions.set(channelName, subscription);

    // Return unsubscribe function
    return () => {
      subscription.unsubscribe();
      this.activeSubscriptions.delete(channelName);
    };
  }

  /**
   * Check for ongoing translation jobs for the current project
   * @param projectId The project ID
   * @returns Active job if found, null otherwise
   */
  public async getActiveJob(projectId: string): Promise<BatchTranslationProgress | null> {
    try {
      const { data, error } = await supabase
        .from('BatchTranslationJobs')
        .select('*')
        .eq('project_id', projectId)
        .in('status', ['pending', 'processing'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error checking for active jobs:', error);
      return null;
    }
  }

  /**
   * Get all active translation jobs for a project, grouped by target language
   * @param projectId The project ID
   * @returns Map of target language to active job
   */
  public async getAllActiveJobs(projectId: string): Promise<Map<string, BatchTranslationProgress>> {
    try {
      const { data, error } = await supabase
        .from('BatchTranslationJobs')
        .select('*')
        .eq('project_id', projectId)
        .in('status', ['pending', 'processing'])
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const activeJobsMap = new Map<string, BatchTranslationProgress>();
      
      if (data) {
        data.forEach(job => {
          // Use target_language as the key
          activeJobsMap.set(job.target_language, job);
        });
      }

      return activeJobsMap;
    } catch (error) {
      console.error('Error checking for all active jobs:', error);
      return new Map();
    }
  }

  /**
   * Get progress for a specific job by ID
   * @param jobId The job ID
   * @returns Job progress or null if not found
   */
  public async getJobProgress(jobId: string): Promise<BatchTranslationProgress | null> {
    try {
      console.log(`🔍 Getting job progress for: ${jobId}`);
      
      const { data, error } = await supabase
        .from('BatchTranslationJobs')
        .select('*')
        .eq('id', jobId)
        .maybeSingle();

      if (error) {
        console.error(`❌ Error getting job progress for ${jobId}:`, error);
        throw error;
      }

      if (data) {
        console.log(`📊 Job progress for ${jobId}:`, {
          status: data.status,
          progress_percentage: data.progress_percentage,
          completed_items: data.completed_items,
          total_items: data.total_items,
          updated_at: data.updated_at
        });
      } else {
        console.log(`❌ No job found for ${jobId}`);
      }
      
      return data;
    } catch (error) {
      console.error('Error getting job progress:', error);
      return null;
    }
  }

  /**
   * Clear completion state cache for a project
   * @param projectId The project ID
   */
  public clearCompletionCache(projectId: string): void {
    const keysToDelete = Array.from(this.completionStates.keys())
      .filter(key => key.startsWith(`${projectId}-`));
    
    keysToDelete.forEach(key => {
      this.completionStates.delete(key);
    });
  }

  /**
   * Unsubscribe from progress updates
   * @param batchId The batch job ID
   */
  private unsubscribe(batchId: string): void {
    const subscription = this.activeSubscriptions.get(batchId);
    if (subscription) {
      subscription.unsubscribe();
      this.activeSubscriptions.delete(batchId);
    }
  }

  /**
   * Clean up all active subscriptions and intervals
   */
  public cleanup(): void {
    // Clean up subscriptions
    const batchIds = Array.from(this.activeSubscriptions.keys());
    for (const batchId of batchIds) {
      this.unsubscribe(batchId);
    }

    // Clean up verification intervals
    const intervals = Array.from(this.verificationIntervals.values());
    intervals.forEach(interval => clearInterval(interval));
    this.verificationIntervals.clear();

    // Clear completion state cache
    this.completionStates.clear();
  }
}

export const batchTranslationService = BatchTranslationService.getInstance();